import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Card,
	Group,
	Loader,
	Pagination,
	Progress,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
} from '@mantine/core'
import { listCheckInMessages } from '../services/actions/messagingActions'

const RECIPIENTS_PAGE_SIZE = 50
const POLL_INTERVAL_MS = 3000
const STATUS_PILLS = [
	{ value: 'QUEUED', label: 'queued', color: 'gray' },
	{ value: 'SENT', label: 'sent', color: 'blue' },
	{ value: 'DELIVERED', label: 'delivered', color: 'green' },
	{ value: 'OPENED', label: 'opened', color: 'grape' },
	{ value: 'BOUNCED', label: 'bounced', color: 'orange' },
	{ value: 'UNSUBSCRIBED', label: 'unsubscribed', color: 'pink' },
	{ value: 'FAILED', label: 'failed', color: 'red' },
]

const toSafeCount = (value) => {
	const parsed = Number(value)
	if (!Number.isFinite(parsed)) return 0
	return Math.max(0, Math.floor(parsed))
}

const formatDateTime = (value) => {
	if (!value) return '—'
	const date = new Date(value)
	if (!Number.isFinite(date.getTime())) return '—'
	return date.toLocaleString()
}

const getRecipientLabel = (recipient) => recipient.address || '—'

export default function CheckInMessagesPage() {
	const { orgSlug, experienceSlug } = useParams()
	const contextKey = `${orgSlug || ''}/${experienceSlug || ''}`
	const lastLoadedContextKeyRef = useRef('')
	const [loading, setLoading] = useState(true)
	const [loadingRows, setLoadingRows] = useState(false)
	const [loadError, setLoadError] = useState('')
	const [statusFilter, setStatusFilter] = useState('')
	const [recipientSearch, setRecipientSearch] = useState('')
	const [recipientPage, setRecipientPage] = useState(1)
	const [data, setData] = useState({
		recipients: [],
		rollups: {},
		live: {},
		totalCount: 0,
		page: 1,
		take: RECIPIENTS_PAGE_SIZE,
	})

	const loadMessages = useCallback(
		async ({ page = 1, initial = false } = {}) => {
			if (initial) {
				setLoading(true)
			} else {
				setLoadingRows(true)
			}
			try {
				setLoadError('')
				const nextPage =
					Number.isFinite(Number(page)) && Number(page) > 0
						? Math.floor(Number(page))
						: 1
				const result = await listCheckInMessages(orgSlug, experienceSlug, {
					status: statusFilter || undefined,
					search: recipientSearch || undefined,
					page: nextPage,
					take: RECIPIENTS_PAGE_SIZE,
				})
				setData({
					recipients: Array.isArray(result?.recipients)
						? result.recipients
						: [],
					rollups: result?.rollups || {},
					live: result?.live || {},
					totalCount: toSafeCount(result?.totalCount),
					page: toSafeCount(result?.page) || nextPage,
					take: toSafeCount(result?.take) || RECIPIENTS_PAGE_SIZE,
				})
				setRecipientPage(nextPage)
			} catch (err) {
				console.error('Failed to load check-in messages:', err)
				setLoadError(err.message || 'Failed to load check-in messages.')
			} finally {
				setLoading(false)
				setLoadingRows(false)
			}
		},
		[orgSlug, experienceSlug, statusFilter, recipientSearch],
	)

	useEffect(() => {
		const isNewContext = lastLoadedContextKeyRef.current !== contextKey
		const debounceMs = !isNewContext && recipientSearch.trim() ? 250 : 0
		const timeoutId = setTimeout(() => {
			lastLoadedContextKeyRef.current = contextKey
			loadMessages({ page: 1, initial: isNewContext })
		}, debounceMs)
		return () => clearTimeout(timeoutId)
	}, [contextKey, statusFilter, recipientSearch, loadMessages])

	const queuedCount = toSafeCount(data.rollups?.queuedCount)
	useEffect(() => {
		if (queuedCount <= 0) return
		let isUnmounted = false
		const intervalId = setInterval(() => {
			if (isUnmounted) return
			if (
				typeof document !== 'undefined' &&
				document.visibilityState !== 'visible'
			) {
				return
			}
			loadMessages({ page: recipientPage })
		}, POLL_INTERVAL_MS)
		return () => {
			isUnmounted = true
			clearInterval(intervalId)
		}
	}, [queuedCount, loadMessages, recipientPage])

	const totalRecipients = toSafeCount(data.rollups?.totalRecipients)
	const sentRecipients = Math.max(0, totalRecipients - queuedCount)
	const progressValue =
		totalRecipients > 0 ? (sentRecipients / totalRecipients) * 100 : 0
	const totalPages = Math.ceil(data.totalCount / RECIPIENTS_PAGE_SIZE)
	const currentStart =
		data.totalCount > 0 ? (recipientPage - 1) * RECIPIENTS_PAGE_SIZE + 1 : 0
	const currentEnd =
		data.totalCount > 0
			? Math.min(
					(recipientPage - 1) * RECIPIENTS_PAGE_SIZE +
						data.recipients.length,
					data.totalCount,
				)
			: 0
	const statusCounts = useMemo(
		() => ({
			QUEUED: data.rollups?.queuedCount || 0,
			SENT: data.rollups?.sentCount || 0,
			DELIVERED: data.rollups?.deliveredCount || 0,
			OPENED: data.rollups?.openedCount || 0,
			BOUNCED: data.rollups?.bouncedCount || 0,
			UNSUBSCRIBED: data.rollups?.unsubCount || 0,
			FAILED: data.rollups?.failedCount || 0,
		}),
		[data.rollups],
	)

	const handlePageChange = async (nextPage) => {
		if (loadingRows) return
		await loadMessages({ page: nextPage })
	}

	if (loading) return <Loader mt='xl' />

	return (
		<Stack spacing='md'>
			<Button
				component={Link}
				to={`/${orgSlug}/experiences/${experienceSlug}/messaging`}
				variant='subtle'
				size='xs'
				style={{ alignSelf: 'flex-start' }}
			>
				Back to Messaging
			</Button>

			<Group position='apart'>
				<Group>
					<Title order={2}>Check-in Messages</Title>
					<Badge color='gray'>transactional</Badge>
				</Group>
				<Button
					variant='default'
					onClick={() => loadMessages({ page: recipientPage })}
					loading={loadingRows}
				>
					Refresh
				</Button>
			</Group>

			{loadError && <Text c='red'>{loadError}</Text>}

			<Card withBorder>
				<Text fw={500}>Delivery Summary</Text>
				<Stack mt='sm' spacing='xs'>
					<Group position='apart' spacing='xs'>
						<Text size='sm'>Processed</Text>
						<Text size='sm' color='dimmed'>
							{sentRecipients.toLocaleString()} /{' '}
							{totalRecipients.toLocaleString()}
						</Text>
					</Group>
					<Progress value={progressValue} color='teal' radius='xl' />
					<Group spacing='xs'>
						<Badge
							variant={statusFilter ? 'outline' : 'filled'}
							color='gray'
							onClick={() => setStatusFilter('')}
							style={{ cursor: 'pointer' }}
						>
							total {totalRecipients}
						</Badge>
						{STATUS_PILLS.map((pill) => (
							<Badge
								key={pill.value}
								color={pill.color}
								variant={statusFilter === pill.value ? 'filled' : 'light'}
								onClick={() =>
									setStatusFilter((current) =>
										current === pill.value ? '' : pill.value,
									)
								}
								style={{ cursor: 'pointer' }}
							>
								{pill.label} {statusCounts[pill.value] ?? 0}
							</Badge>
						))}
					</Group>
				</Stack>
			</Card>

			<Group>
				<TextInput
					placeholder='Search traveler, account, booking, or address'
					value={recipientSearch}
					onChange={(event) => setRecipientSearch(event.currentTarget.value)}
					w={380}
				/>
			</Group>

			<Table striped highlightOnHover>
				<thead>
					<tr>
						<th>Traveler</th>
						<th>Recipient</th>
						<th>Channel</th>
						<th>Template</th>
						<th>Status</th>
						<th>Queued</th>
						<th>Sent</th>
						<th>Delivered</th>
						<th>Opened</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					{loadingRows ? (
						<tr>
							<td colSpan={10}>
								<Stack align='center' spacing='xs'>
									<Loader size='sm' />
									<Text align='center' color='dimmed'>
										Loading check-in messages...
									</Text>
								</Stack>
							</td>
						</tr>
					) : data.recipients.length === 0 ? (
						<tr>
							<td colSpan={10}>
								<Text align='center' color='dimmed'>
									No check-in messages found.
								</Text>
							</td>
						</tr>
					) : (
						data.recipients.map((recipient) => (
							<tr key={recipient.id}>
								<td>{recipient.travelerName || '—'}</td>
								<td>{getRecipientLabel(recipient)}</td>
								<td>{recipient.channel || '—'}</td>
								<td>{recipient.templateLabel || '—'}</td>
								<td>
									<Badge>
										{recipient.status || 'UNKNOWN'}
									</Badge>
									{recipient.errorMessage && (
										<Text size='xs' c='red' mt={4}>
											{recipient.errorMessage}
										</Text>
									)}
								</td>
								<td>{formatDateTime(recipient.queuedAt)}</td>
								<td>{formatDateTime(recipient.sentAt)}</td>
								<td>{formatDateTime(recipient.deliveredAt)}</td>
								<td>{formatDateTime(recipient.openedAt)}</td>
								<td>
									<Group spacing='xs'>
										<Button
											component={Link}
											to={`/${orgSlug}/experiences/${experienceSlug}/checkIn/${recipient.checkInId}/messages`}
											size='xs'
											variant='light'
										>
											History
										</Button>
										<Button
											component={Link}
											to={`/${orgSlug}/experiences/${experienceSlug}/checkIn/${recipient.checkInId}`}
											size='xs'
											variant='default'
										>
											Check-in
										</Button>
									</Group>
								</td>
							</tr>
						))
					)}
				</tbody>
			</Table>

			<Box mt='sm'>
				<Text size='sm' c='dimmed' mb={totalPages > 1 ? 'xs' : 0}>
					Showing {currentStart}-{currentEnd} of {data.totalCount} messages
				</Text>
				{totalPages > 1 && (
					<Pagination
						value={recipientPage}
						onChange={handlePageChange}
						total={totalPages}
						size='sm'
						disabled={loadingRows}
					/>
				)}
			</Box>
		</Stack>
	)
}
